import { FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AutoExposureIcon } from '@dbt-labs/dbt-dag';
import { DropdownButton, DropdownOption } from '@dbt-labs/sourdough';

import { AutoExposureBiProvider } from '@/__generated__/graphql';
import { useFeatureFlag } from '@/context/FeatureFlagProvider';
import { formatBiProviderName } from '@/util/dbt/exposure';

export type AutoBiProviderFilterDropdownProps = {};

export const AUTO_BI_PROVIDER_FILTER_PARAM = 'autoBiProvider';

export const AutoBiProviderFilterDropdown: FC<
  AutoBiProviderFilterDropdownProps
> = () => {
  const isPowerBIAutoExposuresEnabled = useFeatureFlag(
    'meta4178PowerBiautoExposures',
    false,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const options = useMemo(
    () => [
      { label: 'All', value: 'all' },
      ...Object.values(AutoExposureBiProvider)
        .filter(
          (bi) =>
            // if the flag is on, return all options. otherwise filter out powerbi
            isPowerBIAutoExposuresEnabled || bi !== AutoExposureBiProvider.Powerbi,
        )
        .map(
          (l): DropdownOption => ({
            label: (
              <div className="flex flex-row">
                {!!l && (
                  <AutoExposureIcon biProvider={l as any} className="h-5 pr-1.5" />
                )}
                <span>{formatBiProviderName(l)}</span>
              </div>
            ),
            value: l,
          }),
        ),
    ],
    [isPowerBIAutoExposuresEnabled],
  );

  const defaultOption = useMemo((): DropdownOption => {
    const autoBiProviderValue = searchParams.get(AUTO_BI_PROVIDER_FILTER_PARAM);
    return options.find((o) => o.value === autoBiProviderValue) || options[0];
  }, [searchParams, options]);

  return (
    <DropdownButton
      options={options}
      className="text-nowrap text-fgMain"
      defaultOption={defaultOption}
      name="Integration:"
      listClassName="z-50"
      testId="exposure-integration-dropdown"
      onChange={({ value }) => {
        if (typeof value !== 'string') return;
        if (value === 'all') {
          setSearchParams((p) => {
            p.delete(AUTO_BI_PROVIDER_FILTER_PARAM);
            return p;
          });
        } else {
          setSearchParams((p) => {
            p.set(AUTO_BI_PROVIDER_FILTER_PARAM, value);
            return p;
          });
        }
      }}
    />
  );
};
